import { useDisclosure } from "@chakra-ui/hooks";
import { Modal, ModalBody, ModalContent, ModalOverlay } from "@chakra-ui/modal";
import { get2FTAuth, get2FTAuthVerify } from "api/graphQL_users";
import DivLoader from "components/divloader/DivLoader";
import React, { useEffect, useRef } from "react";
import { toast } from "react-hot-toast";
import { useLocation, useNavigate } from "react-router-dom";
import { setToken } from "utils/auth";

const TwoFactorAuthentication = ({
  status, data, profileData, fetch2fa, fetchProfile, model2FA, setModel2FA,
}: {
  status?: any; data?: any; fetch2fa?: () => void;
  profileData?: any; fetchProfile?: () => void;
  model2FA?: any; setModel2FA?: any;
}) => {
  const location = useLocation();
  const verifyData = data;
  const rootForm = { otp: "" };

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isLoading, setIsLoading] = React.useState<boolean>();
  const [copy, setCopy] = React.useState<boolean>(false);
  const [copyKey, setCopyKey] = React.useState<boolean>(false);
  const [formValues, setFormValues] = React.useState<any>(rootForm);
  const [formValuesErr, setFormValuesErr] = React.useState<any>(rootForm);
  const [authCode, setAuthCode] = React.useState(["", "", "", "", "", ""]);
  const inputRefs = useRef<Array<HTMLInputElement | null>>(new Array(6).fill(null));
  const navigate = useNavigate();

  const handleClose = () => {
    setFormValues(rootForm);
    setFormValuesErr(rootForm);
    model2FA && setModel2FA("");
    setAuthCode(["", "", "", "", "", ""]);
    onClose();
  };

  useEffect(() => { if (status) onOpen(); }, [status, model2FA]);

  const handleChange = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (/^\d$/.test(value) || value === "") {
      const newCode = [...authCode];
      newCode[index] = value;
      setAuthCode(newCode);
      if (value !== "" && index < 5 && inputRefs.current[index + 1]) {
        inputRefs.current[index + 1]?.focus();
      }
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && authCode[index] === "" && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").trim();
    if (/^\d{6}$/.test(pastedData)) {
      setAuthCode(pastedData.split(""));
      inputRefs.current[5]?.focus();
    }
  };

  function validateData() {
    const final_otp: any = authCode.join("");
    if (final_otp.length < 6) {
      setFormValuesErr((preval: any) => ({ ...preval, otp: "OTP must be 6 characters" }));
      return false;
    }
    return true;
  }

  async function handleSubmit(e: any) {
    e.preventDefault();
    const final_otp: any = authCode.join("");
    if (!final_otp) { toast.error("OTP is required"); return; }
    if (final_otp.length !== 6) { toast.error("OTP must be 6 characters"); return; }
    if (!validateData()) return;
    try {
      setIsLoading(true);
      await get2FTAuthVerify({
        otp: final_otp,
        user_id: verifyData?.user_id ? verifyData?.user_id : profileData?.id,
        secret_key: verifyData?.secret_key,
        recover_key: model2FA ? model2FA : verifyData?.recover_key,
        inside_flag: model2FA ? true : false,
      })
        .then((data) => {
          if (data.status == "false") {
            toast.error(data.message);
          } else {
            if (model2FA) setToken(data.auth_token);
            get2FTAuth({
              enable_2FA: true,
              secret_key: verifyData?.secret_key,
              recover_key: model2FA ? model2FA : verifyData?.recover_key,
              user_id: verifyData?.user_id ? verifyData?.user_id : profileData?.id,
            })
              .then((data) => {
                handleClose();
                if (model2FA) navigate("/admin/dashboard");
                toast.success("Two-Factor Authentication Activated");
                if (location?.pathname !== "/auth/sign-in") fetchProfile();
              })
              .catch((err) => { toast.error(err?.response?.data?.message ?? "Something went wrong"); });
          }
        })
        .catch((err) => { toast.error(err?.response?.data?.message ?? "Something went wrong"); });
    } catch (err: any) {
      if (err?.response?.status === 422) toast.error("Please provide valid values");
    } finally {
      setIsLoading(false);
    }
  }

  const copyToClipboard = (text: string, setter: (v: boolean) => void) => {
    navigator.clipboard.writeText(text);
    setter(true);
    setTimeout(() => setter(false), 1500);
  };

  return (
    <>
      {/* Trigger */}
      {status ? null : !profileData?.auth_2fa ? (
        <button onClick={onOpen} className="tfa-trigger-btn">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
          Activate 2FA
        </button>
      ) : (
        <button onClick={() => fetch2fa()} className="tfa-trigger-btn tfa-trigger-btn--danger">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
          Deactivate 2FA
        </button>
      )}

      <Modal isOpen={isOpen} onClose={() => {}} isCentered>
        <ModalOverlay className="bg-[#000] !opacity-40" />
        {/* KEY FIX: removed min-w-[480px] and lg:min-w-[520px] — those override CSS on mobile */}
        <ModalContent className="!z-[1002] !m-auto !w-full !max-w-[95%] !p-0 tfa-mc">
          <ModalBody style={{ padding: 0 }}>
            <div className="tfa-modal">

              {/* Header */}
              <div className="tfa-modal-header">
                <div className="tfa-modal-header-left">
                  <div className="tfa-header-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                  </div>
                  <div>
                    <h1 className="tfa-modal-title">Two-Factor Authentication</h1>
                    <p className="tfa-modal-subtitle">Secure your account with 2FA</p>
                  </div>
                </div>
                <button className="tfa-close-btn" onClick={handleClose} aria-label="Close">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              </div>

              {/* Scrollable body */}
              <div className="tfa-modal-body">

                {/* Section 1 — Install */}
                <div className="tfa-section">
                  <div className="tfa-section-header">
                    <div className="tfa-section-num">1</div>
                    <h2 className="tfa-section-title">Configure your authenticator app</h2>
                  </div>
                  <div className="tfa-install-row">
                    {[
                      { name: "Google Authenticator", platforms: "iOS · Android" },
                      { name: "Authy", platforms: "iOS · Android · Desktop" },
                    ].map((app) => (
                      <div key={app.name} className="tfa-app-card">
                        <div className="tfa-app-icon">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><rect x="5" y="2" width="14" height="20" rx="2"/><line x1="12" y1="18" x2="12.01" y2="18"/></svg>
                        </div>
                        <div>
                          <div className="tfa-app-name">{app.name}</div>
                          <div className="tfa-app-platforms">{app.platforms}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <ol className="tfa-steps-list">
                    <li>Install Google Authenticator or Authy on your device.</li>
                    <li>Open the app and tap the <strong>+</strong> icon to add a new account.</li>
                    <li>Choose <em>"Scan a QR code"</em> and point your camera below.</li>
                  </ol>
                </div>

                {/* Section 2 — QR */}
                <div className="tfa-section">
                  <div className="tfa-section-header">
                    <div className="tfa-section-num">2</div>
                    <h2 className="tfa-section-title">Scan QR Code</h2>
                  </div>
                  <div className="tfa-qr-wrap">
                    <div className="tfa-qr-frame">
                      <img src={`data:image/png;base64 , ${data?.base64_image}`} alt="QR Code for 2FA" className="tfa-qr-img" />
                    </div>
                    <div className="tfa-qr-meta">
                      <p className="tfa-qr-hint">Point your authenticator app at this QR code to link your account.</p>
                      <div className="tfa-key-row">
                        <div className="tfa-key-label">Secret Key</div>
                        <div className="tfa-key-value-row">
                          <code className="tfa-key-code">{data?.secret_key}</code>
                          <button className={`tfa-copy-btn${copyKey ? " tfa-copy-btn--copied" : ""}`} onClick={() => copyToClipboard(data?.secret_key, setCopyKey)} title="Copy secret key">
                            {copyKey ? (<><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>Copied</>) : (<><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>Copy</>)}
                          </button>
                        </div>
                      </div>
                      {!model2FA && (
                        <div className="tfa-key-row">
                          <div className="tfa-key-label">Recovery Key<span className="tfa-key-badge">Save this!</span></div>
                          <div className="tfa-key-value-row">
                            <code className="tfa-key-code">{data?.recover_key}</code>
                            <button className={`tfa-copy-btn${copy ? " tfa-copy-btn--copied" : ""}`} onClick={() => copyToClipboard(data?.recover_key, setCopy)} title="Copy recovery key">
                              {copy ? (<><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>Copied</>) : (<><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>Copy</>)}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Section 3 — Verify */}
                <div className="tfa-section tfa-section--last">
                  <div className="tfa-section-header">
                    <div className="tfa-section-num tfa-section-num--active">3</div>
                    <h2 className="tfa-section-title">Verify the code</h2>
                  </div>
                  <p className="tfa-verify-hint">Enter the 6-digit code currently shown in your authenticator app.</p>
                  <div className="tfa-otp-row">
                    {authCode.map((digit, index) => (
                      <input key={index} ref={(el) => (inputRefs.current[index] = el)}
                        type="text" inputMode="numeric" maxLength={1} value={digit}
                        onChange={(e) => handleChange(index, e)}
                        onKeyDown={(e) => handleKeyDown(index, e)}
                        onPaste={index === 0 ? handlePaste : undefined}
                        className={`tfa-otp-box${digit ? " tfa-otp-box--filled" : ""}`}
                        aria-label={`Digit ${index + 1}`}
                      />
                    ))}
                  </div>
                  {formValuesErr.otp && (
                    <p className="tfa-otp-err">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                      {formValuesErr.otp}
                    </p>
                  )}
                </div>
              </div>

              {/* Footer */}
              <div className="tfa-modal-footer">
                <div className="tfa-security-note">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                  2FA adds a critical extra layer of security
                </div>
                <div className="tfa-footer-btns">
                  <button onClick={handleClose} className="tfa-btn-cancel">Cancel</button>
                  <button onClick={handleSubmit} className="tfa-btn-verify" disabled={isLoading}>
                    {isLoading ? (<span className="tfa-spinner" />) : (
                      <><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>Verify &amp; Activate</>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </ModalBody>
        </ModalContent>
      </Modal>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

        /* ── Chakra ModalContent sizing — controlled entirely in CSS ── */
        .tfa-mc {
          max-width: 560px !important;
          width: 560px !important;
        }

        /* ── Trigger buttons ── */
        .tfa-trigger-btn {
          display: inline-flex; align-items: center; gap: 7px;
          font-family: 'Plus Jakarta Sans', sans-serif; font-size: 13.5px; font-weight: 600;
          padding: 9px 18px; border-radius: 10px; cursor: pointer; transition: all 0.18s;
          border: 1.5px solid #bfdbfe; background: #eff6ff; color: #1d4ed8;
        }
        .tfa-trigger-btn:hover { background: #dbeafe; border-color: #93c5fd; }
        .tfa-trigger-btn--danger { border-color: #fecaca; background: #fff7f7; color: #dc2626; }
        .tfa-trigger-btn--danger:hover { background: #fee2e2; border-color: #fca5a5; }

        /* ── Modal shell ── */
        .tfa-modal {
          font-family: 'Plus Jakarta Sans', sans-serif;
          background: #ffffff; border-radius: 20px; overflow: hidden;
          box-shadow: 0 24px 80px rgba(0,0,0,0.16), 0 4px 16px rgba(0,0,0,0.08);
          display: flex; flex-direction: column; max-height: 92vh; width: 100%;
        }

        /* ── Header ── */
        .tfa-modal-header {
          display: flex; align-items: center; justify-content: space-between;
          padding: 22px 28px 20px; border-bottom: 1px solid #f1f5f9;
          background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%); flex-shrink: 0;
        }
        .tfa-modal-header-left { display: flex; align-items: center; gap: 14px; min-width: 0; }
        .tfa-header-icon {
          width: 44px; height: 44px; background: linear-gradient(135deg, #eff6ff, #dbeafe);
          border: 1.5px solid #bfdbfe; border-radius: 12px;
          display: flex; align-items: center; justify-content: center; color: #2563eb; flex-shrink: 0;
        }
        .tfa-modal-title { font-size: 17px; font-weight: 800; color: #0f172a; letter-spacing: -0.3px; margin-bottom: 2px; line-height: 22px; }
        .tfa-modal-subtitle { font-size: 12.5px; color: #94a3b8; font-weight: 500; }
        .tfa-close-btn {
          width: 34px; height: 34px; background: #f1f5f9; border: none; border-radius: 9px;
          cursor: pointer; display: flex; align-items: center; justify-content: center;
          color: #64748b; transition: all 0.15s; flex-shrink: 0; margin-left: 8px;
        }
        .tfa-close-btn:hover { background: #e2e8f0; color: #0f172a; }

        /* ── Body ── */
        .tfa-modal-body { overflow-y: auto; overflow-x: hidden; flex: 1; padding: 0 28px; }
        .tfa-modal-body::-webkit-scrollbar { width: 4px; }
        .tfa-modal-body::-webkit-scrollbar-track { background: #f8fafc; }
        .tfa-modal-body::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }

        /* ── Sections ── */
        .tfa-section { padding: 22px 0; border-bottom: 1px solid #f1f5f9; }
        .tfa-section--last { border-bottom: none; padding-bottom: 8px; }
        .tfa-section-header { display: flex; align-items: center; gap: 10px; margin-bottom: 16px; }
        .tfa-section-num {
          width: 26px; height: 26px; border-radius: 50%; background: #f1f5f9; color: #64748b;
          font-size: 12px; font-weight: 700; display: flex; align-items: center; justify-content: center; flex-shrink: 0;
        }
        .tfa-section-num--active { background: #eff6ff; color: #2563eb; }
        .tfa-section-title { font-size: 14px; font-weight: 700; color: #1e293b; }

        /* ── App cards ── */
        .tfa-install-row { display: flex; gap: 10px; margin-bottom: 14px; flex-wrap: wrap; }
        .tfa-app-card {
          display: flex; align-items: center; gap: 10px;
          background: #f8fafc; border: 1.5px solid #e2e8f0; border-radius: 11px;
          padding: 10px 14px; flex: 1; min-width: 140px; transition: border-color 0.15s;
        }
        .tfa-app-card:hover { border-color: #93c5fd; }
        .tfa-app-icon {
          width: 36px; height: 36px; border-radius: 9px;
          background: linear-gradient(135deg, #eff6ff, #dbeafe);
          display: flex; align-items: center; justify-content: center; color: #2563eb; flex-shrink: 0;
        }
        .tfa-app-name { font-size: 13px; font-weight: 700; color: #0f172a; margin-bottom: 2px; }
        .tfa-app-platforms { font-size: 11px; color: #94a3b8; font-weight: 500; }
        .tfa-steps-list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 6px; }
        .tfa-steps-list li { font-size: 13px; color: #475569; line-height: 1.5; display: flex; gap: 8px; }
        .tfa-steps-list li::before { content: '→'; color: #93c5fd; font-size: 12px; margin-top: 1px; flex-shrink: 0; }

        /* ── QR section ── */
        .tfa-qr-wrap { display: flex; gap: 20px; align-items: flex-start; flex-wrap: wrap; }
        .tfa-qr-frame {
          background: #fff; border: 2px solid #e2e8f0; border-radius: 14px;
          padding: 10px; flex-shrink: 0; box-shadow: 0 2px 12px rgba(0,0,0,0.06);
        }
        .tfa-qr-img { width: 140px; height: 140px; display: block; }
        .tfa-qr-meta { flex: 1; min-width: 180px; display: flex; flex-direction: column; gap: 12px; }
        .tfa-qr-hint { font-size: 13px; color: #64748b; line-height: 1.55; }

        /* Key rows */
        .tfa-key-row { display: flex; flex-direction: column; gap: 5px; }
        .tfa-key-label {
          font-size: 11.5px; font-weight: 700; color: #374151;
          letter-spacing: 0.3px; text-transform: uppercase; display: flex; align-items: center; gap: 6px;
        }
        .tfa-key-badge {
          font-size: 10px; font-weight: 600; text-transform: none;
          background: #fef3c7; color: #92400e; border: 1px solid #fde68a;
          border-radius: 100px; padding: 2px 8px; letter-spacing: 0;
        }
        .tfa-key-value-row {
          display: flex; align-items: center; gap: 8px;
          background: #f8fafc; border: 1.5px solid #e2e8f0; border-radius: 9px; padding: 8px 12px;
        }
        .tfa-key-code {
          font-family: 'Courier New', monospace; font-size: 13px; color: #1e293b; font-weight: 600;
          flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; letter-spacing: 0.5px;
        }
        .tfa-copy-btn {
          display: inline-flex; align-items: center; gap: 5px;
          font-family: 'Plus Jakarta Sans', sans-serif; font-size: 11.5px; font-weight: 600;
          padding: 5px 10px; border-radius: 7px; border: 1.5px solid #e2e8f0;
          background: #fff; color: #64748b; cursor: pointer; transition: all 0.15s; flex-shrink: 0;
        }
        .tfa-copy-btn:hover { border-color: #93c5fd; color: #2563eb; background: #eff6ff; }
        .tfa-copy-btn--copied { border-color: #bbf7d0; color: #15803d; background: #f0fdf4; }

        /* ── OTP ── */
        .tfa-verify-hint { font-size: 13px; color: #64748b; margin-bottom: 18px; line-height: 1.5; }
        .tfa-otp-row { display: flex; gap: 10px; justify-content: center; margin-bottom: 12px; }
        .tfa-otp-box {
          width: 52px; height: 58px; background: #f8fafc; border: 1.5px solid #e2e8f0; border-radius: 13px;
          font-family: 'Plus Jakarta Sans', sans-serif; font-size: 22px; font-weight: 700;
          color: #0f172a; text-align: center; outline: none;
          transition: border-color 0.18s, box-shadow 0.18s, background 0.18s;
          -webkit-appearance: none; caret-color: #2563eb;
        }
        .tfa-otp-box:focus { border-color: #2563eb; background: #fff; box-shadow: 0 0 0 3px rgba(37,99,235,0.1); }
        .tfa-otp-box--filled { border-color: #93c5fd; background: #eff6ff; color: #1d4ed8; }
        .tfa-otp-err { display: flex; align-items: center; justify-content: center; gap: 5px; font-size: 12px; color: #dc2626; font-weight: 500; }

        /* ── Footer ── */
        .tfa-modal-footer {
          display: flex; align-items: center; justify-content: space-between;
          flex-wrap: wrap; gap: 12px; padding: 18px 28px 22px;
          border-top: 1px solid #f1f5f9; background: #fafbfc; flex-shrink: 0;
        }
        .tfa-security-note { display: flex; align-items: center; gap: 6px; font-size: 11px; color: #94a3b8; font-weight: 500; }
        .tfa-footer-btns { display: flex; align-items: center; gap: 10px; margin-left: auto; }
        .tfa-btn-cancel {
          font-family: 'Plus Jakarta Sans', sans-serif; font-size: 13.5px; font-weight: 600;
          padding: 10px 20px; border-radius: 10px; border: 1.5px solid #e2e8f0;
          background: #fff; color: #64748b; cursor: pointer; transition: all 0.15s; white-space: nowrap;
        }
        .tfa-btn-cancel:hover { background: #f1f5f9; border-color: #cbd5e1; color: #374151; }
        .tfa-btn-verify {
          display: inline-flex; align-items: center; gap: 7px;
          font-family: 'Plus Jakarta Sans', sans-serif; font-size: 13.5px; font-weight: 700; color: #fff;
          padding: 10px 22px; border-radius: 10px; border: none;
          background: linear-gradient(135deg, #1d4ed8, #2563eb); cursor: pointer;
          box-shadow: 0 4px 14px rgba(37,99,235,0.3); white-space: nowrap;
          transition: transform 0.15s, box-shadow 0.18s, opacity 0.18s;
          position: relative; overflow: hidden;
        }
        .tfa-btn-verify::after { content: ''; position: absolute; inset: 0; background: linear-gradient(135deg, rgba(255,255,255,0.1), transparent 60%); }
        .tfa-btn-verify:hover { transform: translateY(-1px); box-shadow: 0 8px 24px rgba(37,99,235,0.38); }
        .tfa-btn-verify:active { transform: translateY(0); }
        .tfa-btn-verify:disabled { opacity: 0.6; cursor: not-allowed; transform: none; }
        .tfa-spinner {
          display: inline-block; width: 16px; height: 16px;
          border: 2px solid rgba(255,255,255,0.35); border-top-color: #fff;
          border-radius: 50%; animation: tfaSpin 0.65s linear infinite;
        }
        @keyframes tfaSpin { to { transform: rotate(360deg); } }

        /* ══ RESPONSIVE ══ */

        /* Large desktop (≥1280px) */
        @media (min-width: 1280px) {
          .tfa-mc { max-width: 600px !important; width: 600px !important; }
          .tfa-modal-header { padding: 24px 32px 22px; }
          .tfa-modal-body   { padding: 0 32px; }
          .tfa-modal-footer { padding: 20px 32px 24px; }
          .tfa-modal-title  { font-size: 18px; }
          .tfa-qr-img       { width: 155px; height: 155px; }
        }

        /* Tablet (≤768px) */
        @media (max-width: 768px) {
          .tfa-mc { max-width: 92vw !important; width: 92vw !important; }
          .tfa-modal { border-radius: 18px; }
          .tfa-modal-header { padding: 18px 22px 16px; }
          .tfa-header-icon  { width: 38px; height: 38px; border-radius: 10px; }
          .tfa-modal-title  { font-size: 15px; }
          .tfa-modal-body   { padding: 0 22px; }
          .tfa-modal-footer { padding: 16px 22px 18px; }
          .tfa-section      { padding: 18px 0; }
          .tfa-qr-img       { width: 130px; height: 130px; }
          .tfa-otp-box      { width: 46px; height: 52px; font-size: 20px; }
          .tfa-otp-row      { gap: 8px; }
        }

        /* Mobile (≤560px) */
        @media (max-width: 560px) {
          .tfa-mc {
            max-width: calc(100vw - 24px) !important;
            width: calc(100vw - 24px) !important;
            margin: 12px auto !important;
          }
          .tfa-modal { border-radius: 14px; max-height: 94vh; }
          .tfa-modal-header { padding: 14px 16px 12px; gap: 8px; }
          .tfa-header-icon  { width: 34px; height: 34px; border-radius: 9px; }
          .tfa-header-icon svg { width: 16px; height: 16px; }
          .tfa-modal-title  { font-size: 13.5px; }
          .tfa-modal-subtitle { font-size: 11px; }
          .tfa-close-btn    { width: 30px; height: 30px; border-radius: 8px; }
          .tfa-modal-body   { padding: 0 16px; }
          .tfa-section      { padding: 15px 0; }
          .tfa-section--last { padding-bottom: 14px; }
          .tfa-section-title { font-size: 13px; }
          .tfa-section-header { margin-bottom: 12px; }

          /* App cards: stack vertically */
          .tfa-install-row  { flex-direction: column; gap: 8px; margin-bottom: 10px; }
          .tfa-app-card     { min-width: unset; }
          .tfa-steps-list li { font-size: 12px; }

          /* QR: center QR, meta below */
          .tfa-qr-wrap { flex-direction: column; align-items: center; gap: 14px; }
          .tfa-qr-meta { min-width: unset; width: 100%; }
          .tfa-qr-img  { width: 130px; height: 130px; }
          .tfa-qr-hint { font-size: 12px; }
          .tfa-key-label { font-size: 10.5px; }
          .tfa-key-code  { font-size: 11.5px; }
          .tfa-copy-btn  { font-size: 10.5px; padding: 4px 8px; }

          /* OTP boxes */
          .tfa-otp-box  { width: 42px; height: 48px; font-size: 18px; border-radius: 11px; }
          .tfa-otp-row  { gap: 7px; }
          .tfa-verify-hint { font-size: 12px; margin-bottom: 14px; }

          /* Footer: stack, full-width buttons */
          .tfa-modal-footer { padding: 12px 16px 16px; flex-direction: column; align-items: stretch; gap: 8px; }
          .tfa-security-note { display: none; }
          .tfa-footer-btns  { width: 100%; margin-left: 0; gap: 8px; }
          .tfa-btn-cancel   { flex: 1; text-align: center; padding: 11px 12px; font-size: 13px; border-radius: 9px; }
          .tfa-btn-verify   { flex: 1; justify-content: center; padding: 11px 12px; font-size: 13px; border-radius: 9px; }
        }

        /* Very small (≤380px) */
        @media (max-width: 380px) {
          .tfa-mc {
            max-width: calc(100vw - 16px) !important;
            width: calc(100vw - 16px) !important;
            margin: 8px auto !important;
          }
          .tfa-modal { border-radius: 12px; }
          .tfa-modal-header { padding: 12px 13px 10px; }
          .tfa-modal-body   { padding: 0 13px; }
          .tfa-modal-footer { padding: 10px 13px 13px; }
          .tfa-modal-title  { font-size: 13px; }
          .tfa-header-icon  { width: 30px; height: 30px; }
          .tfa-close-btn    { width: 27px; height: 27px; }
          .tfa-otp-box      { width: 37px; height: 43px; font-size: 16px; border-radius: 9px; }
          .tfa-otp-row      { gap: 5px; }
          .tfa-btn-cancel, .tfa-btn-verify { font-size: 12px; padding: 10px 8px; }
          .tfa-qr-img       { width: 115px; height: 115px; }
        }
      `}</style>
    </>
  );
};

export default TwoFactorAuthentication;